import { useEffect, useMemo, useState } from 'react';
import { useFetcher } from '@remix-run/react';
import { useDebounceFn } from 'ahooks';
import type { SupportedCountry } from 'generated/graphql';
import { FindPlaceApiProviderEnum, FindPlaceTypeEnum } from 'generated/graphql';
import {
    getPlaceDropdownOptions,
    getRegionNameByCountry,
} from '~/modules/location/util';
import type { DetailedPlace } from '~/modules/location/type';
import { FIND_PLACE_REMIX_API } from '~/routes/remix-api.location.find-place-by-keyword';
import type { loader } from '~/routes/remix-api.location.find-place-by-keyword';
import { useCountryContext } from '@archipro-website/localisation';

interface UsePlaceFinderProps {
    /** The maximum number of locations to return. Defaults to `10`. */
    limit?: number;
    /** The type of locations to return. Defaults to `FindPlaceTypeEnum.SuburbMixed`. */
    type?: FindPlaceTypeEnum;
    /** The country to search in. Defaults to the site country. */
    country?: SupportedCountry;
    /** The API provider to use. Defaults to `FindPlaceApiProviderEnum.Mapbox`. */
    apiProvider?: FindPlaceApiProviderEnum;
    /** The debounce time in milliseconds. Defaults to `500`. */
    debounceMs?: number;
}

// doc: https://archipro.atlassian.net/wiki/spaces/AD/pages/1777139713/Place+Finder
// gql: packages/web/app/modules/location/graphql/query/find-place.graphql
// api: /remix-api/location/find-place-by-keyword

/**
 * Hook to search for a list of locations by keyword.
 * @todo Merge with `usePlaceFinder` in ui-rocco.
 */
export function usePlaceFinder({
    limit = 10,
    type = FindPlaceTypeEnum.SuburbMixed,
    country,
    apiProvider = FindPlaceApiProviderEnum.Mapbox,
    debounceMs = 500,
}: UsePlaceFinderProps) {
    const fetcher = useFetcher<typeof loader>();
    const [places, setPlaces] = useState<DetailedPlace[]>([]);
    const { code: countryCode } = useCountryContext();
    const searchCountry = useMemo(
        () => country ?? countryCode,
        [country, countryCode],
    );
    const dropdownOptions = useMemo(
        () => getPlaceDropdownOptions(places),
        [places],
    );
    const searchFilterOptions = useMemo(
        () => getSearchFilterOptions(places),
        [places],
    );

    const { run: changeKeyword } = useDebounceFn(
        async (newKeyword?: string) => {
            if (!newKeyword || newKeyword.trim().length < 2) {
                setPlaces([]);
                return;
            }
            try {
                const formData = new FormData();
                formData.append('keyword', newKeyword.trim());
                formData.append('type', type.toString());
                formData.append('limit', limit.toString());
                formData.append('country', searchCountry.toString());
                formData.append('apiProvider', apiProvider.toString());
                fetcher.submit(formData, {
                    method: 'get',
                    action: FIND_PLACE_REMIX_API,
                });
            } catch (error) {
                console.error(error);
                setPlaces([]);
            }
        },
        { wait: debounceMs },
    );

    useEffect(() => {
        setPlaces(fetcher?.data ?? []);
    }, [fetcher.data]);

    return {
        /** The list of places/locations fetched from the API. */
        data: places,
        /** The loading state of the fetcher. */
        loading: fetcher.state !== 'idle',
        /** The function to update the search keyword. */
        changeKeyword,
        /**
         * The places/locations in the dropdown options format. Used for
         * address/postcode/location dropdowns.
         */
        dropdownOptions,
        /**
         * The places/locations in the search filter options format. Used for
         * search filter dropdowns.
         */
        searchFilterOptions,
    };
}

/**
 * Format a location/place to a readable string for search filter dropdown options.
 * @param place The place/location to format.
 * @returns A readable string for search filter dropdown options.
 */
export function formatLocationSearchFilterText(place: {
    placeType?: DetailedPlace['placeType'];
    suburb?: string;
    district?: string;
    city?: string;
    region?: string;
    state?: string;
    country?: string;
}) {
    let placeType = place.placeType;
    if (!placeType && !place.suburb && place.district) {
        placeType = 'district';
    }
    const segments = [
        place.suburb?.trim(),
        // Show only city or district, unless district type.
        ...(placeType === 'district'
            ? [place.district?.trim(), place.city?.trim()]
            : [place.city?.trim() || place.district?.trim()]),
        place.region?.trim(),
        // Do not show state for New Zealand (North Island and South Island).
        place.country !== 'New Zealand' ? place.state?.trim() : '',
    ].filter(Boolean);

    return [...new Set(segments)].join(', ');
}

/**
 * Generate dropdown options for search filters.
 * @param places The places/locations fetched from the API.
 * @returns A list of dropdown options for search filters.
 */
function getSearchFilterOptions(places: DetailedPlace[]) {
    return places.map((place) => ({
        header: formatLocationSearchFilterText(place),
        value: place,
        placeType: formatPlaceType(place.placeType, place.country),
    }));
}

function formatPlaceType(
    placeType: DetailedPlace['placeType'],
    country?: string,
) {
    switch (placeType) {
        case 'address':
            return 'Address';
        case 'postcode':
            return 'Postcode';
        case 'suburb':
            return 'Suburb';
        case 'district':
            return 'District';
        case 'city':
            return 'City';
        case 'region':
            // US admin1 areas come back as `region` but are labelled `State`.
            return getRegionNameByCountry(country) || 'Region';
        case 'state':
            return 'State';
        case 'country':
            return 'Country';
        default:
            return 'Other';
    }
}
